/**
 * Hearings API — talks to the backend.
 * Translates rich API data into the flat Hearing shape the frontend pages use.
 */
import { api } from './api';
import type { Hearing } from './hearingsStore';

export type DiaryConflictKind = 'overlap' | 'adjacent';

export interface DiaryConflict {
  id: string;
  title: string;
  eventType: string;
  startAt: string;
  endAt: string;
  kind: DiaryConflictKind;
  gapMinutes?: number;
}

/** Authoritative diary-conflict check (covers synced Google/Outlook events). Advisory only. */
export async function checkHearingConflicts(payload: {
  caseId: string;
  startAt: string;
  durationMinutes: number;
  excludeHearingId?: string;
}): Promise<DiaryConflict[]> {
  const res = await api.post<{ data: { conflicts: DiaryConflict[] } }>('/hearings/check-conflicts', payload);
  return res.data.data.conflicts;
}

type BarristerUserSnippet = {
  user: { id: string; firstName: string; lastName: string; memberType: string };
} | null;

interface ApiHearing {
  id: string;
  caseId: string;
  hearingDate: string;
  durationMinutes: number;
  courtroom: string | null;
  hearingType: string;
  status: string;
  outcome: string | null;
  outcomeType: string | null;
  judgmentReserved: boolean;
  costsOrdered: boolean;
  judicialRemarks: string | null;
  courtOrders: string | null;
  counselNotes: string | null;
  costsPosition: string | null;
  courtDeadlines: string | null;
  nextHearingDate: string | null;
  advocateNotes: string | null;
  preparationChecklist: Record<string, boolean> | null;
  notes: string | null;
  isVirtual: boolean;
  videoLink: string | null;
  createdAt: string;
  updatedAt: string;
  case: {
    id: string;
    caseNumber: string;
    caseName: string;
    status: string;
    leadBarrister: BarristerUserSnippet;
    juniorBarrister: BarristerUserSnippet;
  } | null;
  court: { id: string; name: string; shortName: string; type: string } | null;
  judge: { id: string; title: string; fullName: string; shortName: string } | null;
  judgeName?: string | null;
}

// ─── Helpers ────────────────────────────────────────────────

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (mins >= 480) return `${Math.ceil(mins / 480)} days`;
  return m ? `${h}h ${m}m` : `${h} hour${h > 1 ? 's' : ''}`;
}

function hearingTypeLabel(type: string): string {
  const map: Record<string, string> = {
    first_listing: 'First listing',
    case_management: 'CMC',
    pre_trial_review: 'PTR',
    ptph: 'PTPH',
    interlocutory: 'Application',
    trial: 'Trial',
    sentencing: 'Sentencing',
    appeal: 'Appeal',
    mention: 'Mention',
    bail: 'Bail',
    costs: 'Costs',
    mediation: 'Mediation',
    other: 'Hearing',
  };
  return map[type] ?? type;
}

function statusToFrontend(s: string): Hearing['status'] {
  const map: Record<string, Hearing['status']> = {
    scheduled:           'scheduled',
    confirmed:           'confirmed',
    in_progress:         'in_progress',
    awaiting_appearance: 'awaiting_appearance',
    awaiting_compliance: 'awaiting_compliance',
    adjourned:           'adjourned',
    order_reserved:      'order_reserved',
    judgment_pending:    'judgment_pending',
    completed:           'completed',
    vacated:             'vacated',
    no_show:             'no_show',
    disposed:            'disposed',
  };
  return map[s] ?? 'scheduled';
}

function formatBarristerName(b: BarristerUserSnippet): string {
  if (!b) return '';
  const suffix = b.user.memberType === 'silk' ? ' KC' : '';
  return `${b.user.firstName} ${b.user.lastName}${suffix}`;
}

function apiToHearing(h: ApiHearing): Hearing {
  const dt = new Date(h.hearingDate);
  return {
    id: h.id,
    matterId: h.caseId,
    matterName: h.case?.caseName ?? '',
    matterNumber: h.case?.caseNumber ?? '',
    date: dt.toISOString().split('T')[0] ?? '',
    // Hearing times are stored as timezone-stable wall-clock (written with a trailing "Z"),
    // so read them back in UTC too — otherwise the browser's local zone shifts the displayed
    // time away from what the user picked (e.g. 07:00 rendering as 12:30).
    time: dt.toISOString().slice(11, 16),
    durationLabel: formatDuration(h.durationMinutes),
    durationMinutes: h.durationMinutes,
    type: hearingTypeLabel(h.hearingType),
    status: statusToFrontend(h.status),
    court: h.court?.shortName ?? '',
    courtId: h.court?.id ?? null,
    courtroom: h.courtroom ?? '',
    // Prefer the structured Judge relation when set; otherwise fall back to the free-text
    // judgeName recorded on the listing (WORK-1).
    judge: h.judge?.shortName ?? h.judge?.fullName ?? h.judgeName ?? '',
    judgeId: h.judge?.id ?? null,
    leadBarrister: formatBarristerName(h.case?.leadBarrister ?? null),
    leadBarristerUserId: h.case?.leadBarrister?.user.id ?? null,
    juniorBarrister: formatBarristerName(h.case?.juniorBarrister ?? null),
    juniorBarristerUserId: h.case?.juniorBarrister?.user.id ?? null,
    isVirtual: h.isVirtual,
    videoLink: h.videoLink ?? '',
    notes: h.notes ?? '',
    outcome: h.outcome ?? '',
    outcomeType: h.outcomeType ?? undefined,
    judgmentReserved: h.judgmentReserved,
    costsOrdered: h.costsOrdered,
    judicialRemarks: h.judicialRemarks ?? undefined,
    orders: h.courtOrders ?? '',
    counselNotes: h.counselNotes ?? '',
    costsPosition: h.costsPosition ?? undefined,
    deadlines: h.courtDeadlines ?? undefined,
    advocateNotes: h.advocateNotes ?? undefined,
    preparationChecklist: h.preparationChecklist ?? undefined,
    nextHearingDate: h.nextHearingDate ? (new Date(h.nextHearingDate).toISOString().split('T')[0] ?? '') : '',
    createdAt: new Date(h.createdAt).getTime(),
  };
}

// ─── API calls ──────────────────────────────────────────────

/** Page size the list endpoint allows (listHearingsSchema max). */
const HEARINGS_PAGE_SIZE = 100;
/** Safety bound on pages fetched for the shared store (5,000 hearings). */
const HEARINGS_MAX_PAGES = 50;

/**
 * Every hearing the caller may see, oldest first — the shared hearings store.
 *
 * The list endpoint caps a page at 100. This used to request one page, so a chambers with more
 * than 100 hearings silently lost the newest ones: upcoming hearings vanished from the hearings
 * list, dashboards and Court Day, and their detail pages said "Hearing not found". It now walks
 * the server's pagination until every page is loaded (bounded by HEARINGS_MAX_PAGES).
 */
export async function fetchHearings(): Promise<Hearing[]> {
  const all: ApiHearing[] = [];
  for (let page = 1; page <= HEARINGS_MAX_PAGES; page++) {
    const res = await api.get('/hearings', {
      params: { page, limit: HEARINGS_PAGE_SIZE, sortBy: 'hearingDate', sortOrder: 'asc' },
    });
    const payload = res.data?.data ?? res.data;
    const batch: ApiHearing[] = payload.hearings ?? [];
    all.push(...batch);
    const totalPages: number | undefined = payload.pagination?.totalPages;
    if (batch.length < HEARINGS_PAGE_SIZE || (totalPages !== undefined && page >= totalPages)) break;
  }
  return all.map(apiToHearing);
}

/** Server-side hearing search (matter name/number, judge, court) — finds hearings
 *  beyond the client-loaded window. Access-scoped server-side. */
export async function searchHearings(query: string): Promise<Hearing[]> {
  const res = await api.get('/hearings', {
    params: { search: query, limit: 50, sortBy: 'hearingDate', sortOrder: 'desc' },
  });
  const payload = res.data?.data ?? res.data;
  const hearings: ApiHearing[] = payload.hearings ?? [];
  return hearings.map(apiToHearing);
}

export async function fetchHearing(id: string): Promise<Hearing> {
  const res = await api.get(`/hearings/${id}`);
  const hearing = (res.data as any).data ?? res.data;
  return apiToHearing(hearing as ApiHearing);
}

export interface CreateHearingPayload {
  caseId: string;
  hearingDate: string;
  durationMinutes?: number;
  courtId?: string;
  courtroom?: string;
  judgeId?: string;
  judge?: string;
  hearingType: string;
  notes?: string;
  isVirtual?: boolean;
  videoLink?: string;
  /** Recording an already-happened hearing — allows a past date, skips diary events/reminders. */
  backdated?: boolean;
}

export async function createHearing(payload: CreateHearingPayload): Promise<Hearing> {
  const res = await api.post('/hearings', payload);
  const hearing = (res.data as any).data ?? res.data;
  return apiToHearing(hearing as ApiHearing);
}

export async function updateHearing(id: string, payload: Record<string, unknown>): Promise<Hearing> {
  const res = await api.patch(`/hearings/${id}`, payload);
  const hearing = (res.data as any).data ?? res.data;
  return apiToHearing(hearing as ApiHearing);
}

export async function deleteHearing(id: string): Promise<void> {
  await api.delete(`/hearings/${id}`);
}

// ─── Hearing Readiness ──────────────────────────────────────

export interface ReadinessChecklistItem {
  id: string;
  label: string;
  completed: boolean;
  completedAt?: string;
  completedBy?: string;
  notes?: string;
  requiredRole?: 'barrister' | 'clerk' | 'any';
}

export interface HearingReadiness {
  status: 'not_started' | 'in_progress' | 'ready' | 'needs_attention';
  percentage: number;
  items: ReadinessChecklistItem[];
  lastUpdatedAt: string;
  lastUpdatedBy?: string;
  missingItems: string[];
  summary: string;
}

export interface HearingNeedingAttention {
  hearingId: string;
  matterId: string;
  matterName: string;
  hearingDate: string;
  percentage: number;
  missingCount: number;
}

export async function fetchHearingReadiness(hearingId: string): Promise<HearingReadiness> {
  const res = await api.get(`/hearings/${hearingId}/readiness`);
  return (res.data as any).data;
}

export async function updateHearingReadinessItem(
  hearingId: string,
  itemId: string,
  completed: boolean,
  notes?: string,
): Promise<HearingReadiness> {
  const res = await api.patch(`/hearings/${hearingId}/readiness/${itemId}`, {
    completed,
    notes,
  });
  return (res.data as any).data;
}

export async function fetchHearingsNeedingAttention(params?: {
  barristerId?: string;
  daysAhead?: number;
}): Promise<HearingNeedingAttention[]> {
  const res = await api.get('/hearings/needing-attention', { params });
  return (res.data as any).data;
}
